import React, { CSSProperties, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logout } from "../services/auth.service";
import YogyismLogoSvg from "../components/tutorial/yogyismLogoSvg";
import LoginModal from "./tutorial/LoginModal";
import ContactModal from "./tutorial/Contactmodal";
import SocialQrModal from "./tutorial/SocialQrModal";
import { FaInstagram as FaInstagramRaw, FaWhatsapp as FaWhatsappRaw } from "react-icons/fa";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

// react-icons v5's IconType returns ReactNode, which React 19's stricter JSX
// component typing rejects — cast once here rather than at every call site.
const FaInstagram = FaInstagramRaw as unknown as React.FC<{ size?: number }>;
const FaWhatsapp  = FaWhatsappRaw  as unknown as React.FC<{ size?: number }>;

// ── Constants ─────────────────────────────────────────────────────────────────
const ACCENT     = "#378add";
const ACCENT_RGB = "55,138,221";
const FONT       = FONT_BODY;
const MONO       = FONT_MONO;

// TODO: replace with the real Instagram handle and WhatsApp number.
const SOCIAL_LINKS = {
  instagram: "https://instagram.com/yogyism",
  whatsapp:  "https://chat.whatsapp.com/EIASdNv9ujzFKnGFmPfxBp",
};

// ── Consent helpers ───────────────────────────────────────────────────────────
const CONSENT_KEY     = "yogyism:consentAccepted:v1";
const CONSENT_VERSION = "v1";

function hasConsented(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    return JSON.parse(raw)?.version === CONSENT_VERSION;
  } catch { return false; }
}

function writeConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      acceptedAt: Date.now(),
    }));
  } catch {}
}

// ── Consent Modal ─────────────────────────────────────────────────────────────
function ConsentModal({
  onAccept,
  onDecline,
}: {
  onAccept: () => void;
  onDecline: () => void;
}) {
  const [checked,     setChecked]     = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(3,7,18,0.93)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, fontFamily: MONO,
    }}>
      <div style={{
        background: "#0b1628", border: "1px solid #1e3a5f", borderRadius: 18,
        width: "min(460px, 96vw)", maxHeight: "90vh", overflowY: "auto",
        padding: "22px 22px 18px", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        scrollbarWidth: "thin", scrollbarColor: "#1e3a5f transparent",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 26 }}>✨</span>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", margin: 0 }}>
            Before you login
          </h2>
        </div>

        {[
          "This app is an experimental, personal project exploring sleep and dream sensing — built for learning and exploration, not as a medical product.",
          "It plays gentle audio cues during your sleep based on sensor readings. Since this is still experimental, detection won't always be perfect. If you ever feel uncomfortable, you can stop at any time.",
          "This is meant for personal experimentation and isn't a substitute for professional medical or mental health care.",
        ].map((text, i) => (
          <p key={i} style={{
            fontSize: 13, color: "#a8bdd1", lineHeight: 1.55,
            margin: i === 0 ? "0 0 8px" : "8px 0",
          }}>{text}</p>
        ))}

        <button
          onClick={() => setShowDetails(v => !v)}
          style={{
            alignSelf: "flex-start", background: "none", border: "none",
            color: "#38bdf8", fontSize: 12, fontWeight: 700, cursor: "pointer",
            padding: "4px 0", marginTop: 2,
          }}
          type="button"
        >
          {showDetails ? "Hide full details ▲" : "Read full details ▾"}
        </button>

        {showDetails && (
          <div style={{
            background: "#07101e", border: "1px solid #1e293b",
            borderRadius: 12, padding: "14px 16px", marginTop: 8,
          }}>
            {[
              { title: "What this is",         body: "An independent, personal engineering project exploring EOG/eye-movement sensing, sleep-stage estimation, and audio playback timed to detected REM (dreaming) activity." },
              { title: "Not a medical device", body: "Not a certified medical device, and not intended to diagnose, treat, cure, or prevent any medical or psychological condition." },
              { title: "Your own risk",        body: "Use of this app is voluntary. If you ever feel distressed or unwell, stop using the app and reach out to a trusted person or professional." },
              { title: "No warranty",          body: 'Provided "as is," without warranty of any kind. The developer(s) accept no liability for outcomes from its use.' },
            ].map(({ title, body }) => (
              <div key={title} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: "#38bdf8", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>{title}</div>
                <div style={{ fontSize: 12, color: "#a8bdd1", lineHeight: 1.55 }}>{body}</div>
              </div>
            ))}
          </div>
        )}

        <label style={{ display: "flex", alignItems: "flex-start", gap: 9, marginTop: 16, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{ marginTop: 2, width: 16, height: 16, accentColor: "#38bdf8", cursor: "pointer", flexShrink: 0 }}
          />
          <span style={{ fontSize: 12.5, color: "#cfe3f5", lineHeight: 1.4 }}>
            I understand this is an experimental personal project, and I'm happy to continue at my own discretion.
          </span>
        </label>

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button
            onClick={onDecline}
            style={{
              background: "transparent", color: "#94a3b8",
              border: "1px solid #334155", borderRadius: 10,
              padding: "11px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            Not now
          </button>
          <button
            onClick={onAccept}
            disabled={!checked}
            style={{
              flex: 1, background: "#38bdf8", color: "#031018",
              border: "none", borderRadius: 10, padding: "11px 18px",
              fontSize: 13, fontWeight: 800,
              opacity: checked ? 1 : 0.4,
              cursor: checked ? "pointer" : "not-allowed",
            }}
          >
            Continue to login →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── External link icon ────────────────────────────────────────────────────────
function ExternalLinkIcon({ size = 10, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg" width={size} height={size}
      viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// ── Star positions (mirrors SlideToolbar) ─────────────────────────────────────
const HEADER_STARS: [number, number, string, number][] = [
  [30,  18, ACCENT,    0.18], [85,   8, "white", 0.28], [160, 30, "white", 0.18],
  [240, 12, "#b0a8f0", 0.20], [320, 25, "white", 0.22], [400,  7, "white", 0.28],
  [560, 15, "white",   0.24], [640, 28, "white", 0.20], [720, 10, "#b0a8f0", 0.18],
  [800, 22, "white",   0.20], [860, 40, "white", 0.22],
];

// ── Types ─────────────────────────────────────────────────────────────────────
type HeaderProps = {
  user: {
    photoURL?:    string | null;
    displayName?: string | null;
    email?:       string | null;
  } | null;
  bothConnected?:    boolean;
  onLogout?: () => void;
};

// ── Mobile overflow menu ─────────────────────────────────────────────────────
// Same pattern as SlideToolbar's MobileHeaderMenu: collapses Support Us /
// Contact Us / user info / Login-Logout into a single "⋯" button so the
// centered "Dreamfulness" title never has to share a row with them.
//
// FIX: dropdown now uses `position: fixed`, anchored to the button's live
// on-screen coordinates via getBoundingClientRect(). Previously it used
// `position: absolute`, which several scrollable/overflow-hidden ancestors
// (`.right-panel`, `.left-panel`, the practice swiper container, etc.)
// were clipping or shifting — that was the misalignment. `position: fixed`
// is measured against the viewport instead, so it always lands in the
// right spot and is always fully visible, no matter what ancestor it's
// rendered inside. Same fix already used for the info tooltips in setup.tsx.
const MENU_WIDTH = 220;

function MobileHeaderMenu({
  user, onLogin, onLogout, onContact,
}: {
  user: HeaderProps["user"];
  onLogin: () => void;
  onLogout: () => void;
  onContact: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const computeCoords = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    let left = rect.right - MENU_WIDTH;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));
    setCoords({ top: rect.bottom + 8, left });
  };

  const handleToggle = () => {
    if (!open) computeCoords();
    setOpen(v => !v);
  };

  useEffect(() => {
    if (!open) return;
    const reposition = () => computeCoords();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const itemStyle: CSSProperties = {
    display: "flex", alignItems: "center", gap: 8,
    width: "100%", textAlign: "left",
    background: "transparent", border: "none",
    color: "#e8f4fd", fontFamily: MONO, fontSize: 12.5,
    letterSpacing: 0.4, padding: "10px 12px", borderRadius: 8,
    cursor: "pointer", textDecoration: "none",
  };

  return (
    <div style={{ position: "relative", zIndex: 5, justifySelf: "end" }}>
      <button
        ref={btnRef}
        aria-label="Menu"
        aria-expanded={open}
        onClick={handleToggle}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 34, height: 34, borderRadius: 10,
          background: open ? `rgba(${ACCENT_RGB},0.16)` : "rgba(255,255,255,0.04)",
          border: `1px solid rgba(${ACCENT_RGB},${open ? 0.6 : 0.32})`,
          color: "white", cursor: "pointer", transition: "all 0.15s ease",
        }}
      >
        {user?.photoURL ? (
          <img src={user.photoURL} alt="" referrerPolicy="no-referrer" style={{ width: 24, height: 24, borderRadius: "50%" }} />
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="4" y1="7" x2="20" y2="7" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="17" x2="20" y2="17" />
          </svg>
        )}
      </button>

      {open && coords && (
        <>
          {/* click-away catcher — fixed + full viewport so a tap anywhere
              else closes the menu regardless of which scrollable ancestor
              this component happens to be rendered inside */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 998 }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed", top: coords.top, left: coords.left, zIndex: 999,
              minWidth: MENU_WIDTH, background: "#0b1a2c",
              border: `1px solid rgba(${ACCENT_RGB},0.28)`,
              borderRadius: 14, padding: 6,
              boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
              display: "flex", flexDirection: "column", gap: 2,
              animation: "mobileMenuFadeIn 0.16s ease",
            }}
          >
            <style>{`@keyframes mobileMenuFadeIn { from { opacity:0; transform: translateY(-4px);} to { opacity:1; transform: translateY(0);} }`}</style>

            {user && (
              <div style={{ padding: "8px 12px 10px", borderBottom: `1px solid rgba(${ACCENT_RGB},0.16)`, marginBottom: 4 }}>
                <div style={{ fontSize: 12.5, color: "#e8f4ff", fontFamily: FONT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user.displayName}
                </div>
                <div style={{ fontSize: 10.5, color: "#7aa8cc", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user.email}
                </div>
              </div>
            )}

            <a
              href="https://paypal.me/MadhanKumarDorairaj" target="_blank" rel="noopener noreferrer"
              style={itemStyle} onClick={() => setOpen(false)}
            >
              <span style={{ color: "#ff5a6e" }}>♥</span> Support Us
            </a>

            <button style={itemStyle} onClick={() => { onContact(); setOpen(false); }}>
              💬 Contact Us
            </button>

            <div style={{ height: 1, background: `rgba(${ACCENT_RGB},0.18)`, margin: "4px 6px" }} />

            {!user ? (
              <button style={{ ...itemStyle, color: ACCENT, fontWeight: 700 }} onClick={() => { onLogin(); setOpen(false); }}>
                → Login
              </button>
            ) : (
              <button style={{ ...itemStyle, color: "#ff9a9a", fontWeight: 700 }} onClick={() => { onLogout(); setOpen(false); }}>
                ⎋ Logout
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function Header({
  user,
  bothConnected    = false,
  onLogout,
}: HeaderProps) {
  const [logoHovered,  setLogoHovered]  = useState(false);
  const [showLogin,    setShowLogin]    = useState(false);
  const [showContact,  setShowContact]  = useState(false);
  const [showConsent,  setShowConsent]  = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 640
  );

  const navigate = useNavigate();

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const logoColor = logoHovered ? "#5fb3ff" : "white";

  // ── Button shared styles — matches SlideToolbar exactly ──────────────────
  const btnBase: CSSProperties = {
    background:    "transparent",
    border:        `1px solid rgba(${ACCENT_RGB},0.55)`,
    padding:       "5px 14px",
    borderRadius:  6,
    color:         "white",
    cursor:        "pointer",
    fontSize:      "clamp(11px,0.9vw,14px)",
    fontFamily:    MONO,
    letterSpacing: 1,
    textTransform: "uppercase",
    transition:    "border-color 0.2s, color 0.2s",
    opacity:       0.85,
  };

  const btnHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.opacity     = "1";
    e.currentTarget.style.borderColor = `rgba(${ACCENT_RGB},0.75)`;
  };
  const btnLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.opacity     = "0.85";
    e.currentTarget.style.borderColor = `rgba(${ACCENT_RGB},0.55)`;
  };

  // ── Login click: check consent first ──────────────────────────────────────
  const handleLoginClick = () => {
    setShowLogin(true);
  };

  const handleConsentAccept = () => {
    writeConsent();
    setShowConsent(false);
    setShowLogin(true);
  };

  const handleConsentDecline = () => {
    setShowConsent(false);
  };

  const handleLogout = async () => {
    try {
      onLogout?.();
      await logout();
      navigate("/home");
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  // ── Header is a 3-column CSS Grid (logo | title | actions), same pattern as
  //    SlideToolbar.
  //
  //    Previously this used "auto 1fr auto": the two outer columns just take
  //    whatever width their own content needs, and the title's middle column
  //    (1fr) fills whatever is left over — so the title only ends up in the
  //    true center of the header when the logo column and the actions column
  //    happen to be the same width. On mobile that's essentially never true:
  //    the logo is icon-only (~34px) while the "⋯" overflow-menu button is a
  //    different width, so "Dreamfulness" would sit visibly off-center,
  //    nudged toward whichever side is narrower.
  //
  //    Switching to "1fr auto 1fr" forces both outer columns to be equal
  //    width (they share the leftover space evenly), so the middle (title)
  //    column is always the true visual center of the header — on mobile and
  //    desktop alike. The logo and actions columns are pinned to the start
  //    and end of their (now-equal) columns via justifySelf so they still
  //    hug the edges rather than centering themselves.
  const headerStyle: CSSProperties = {
    display:        "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems:     "center",
    columnGap:      8,
    padding:        "5px clamp(10px,2vw,24px)",
    background:     "#071520",
    borderBottom:   `1px solid rgba(${ACCENT_RGB},0.22)`,
    position:       "relative",
    flexShrink:     0,
  };

  return (
    <>
      {/* ── Main header bar ── */}
      <div style={headerStyle}>

        {/* ── Star background ── */}
        <div style={{
          position: "absolute", top: 0, left: 0,
          width: "100%", height: "100%",
          overflow: "hidden", pointerEvents: "none",
        }}>
          <svg style={{ width: "100%", height: "100%" }} viewBox="0 0 900 60" preserveAspectRatio="xMidYMid slice">
            {HEADER_STARS.map(([cx, cy, fill, opacity], i) => (
              <circle key={i} cx={cx} cy={cy} r="1" fill={fill} opacity={opacity} />
            ))}
          </svg>
        </div>

        {/* ── Left: Logo — grid column 1, pinned to the start ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "clamp(8px,1vw,14px)", zIndex: 1, minWidth: 0, justifySelf: "start" }}>
          <a
            href="https://www.yogyism.org/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "none",
              display:        "flex",
              alignItems:     "center",
              gap:            "clamp(6px,1vw,12px)",
              cursor:         "pointer",
              minWidth:       0,
            }}
            onMouseEnter={() => setLogoHovered(true)}
            onMouseLeave={() => setLogoHovered(false)}
          >
            {/* Logo circle */}
            <div style={{
              width:          "clamp(24px,3.5vw,50px)",
              height:         "clamp(24px,3.5vw,50px)",
              background:     "#0c2a44",
              border:         "1px solid rgba(55,138,221,0.35)",
              borderRadius:   "50%",
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              overflow:       "hidden",
              flexShrink:     0,
            }}>
               {!isMobile && (
               <YogyismLogoSvg width="100%" height="100%" color="white" style={{ marginTop: "15px" }} />
               )}
                {isMobile && (
                      <YogyismLogoSvg width="100%" height="100%" color="white" style={{ marginTop: "10px" }} />
               )}
            </div>

            {/* Logo text — hidden on mobile to save room for the title */}
            {!isMobile && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                <span style={{
                  fontSize:         "clamp(15px,1.5vw,22px)",
                  fontWeight:       600,
                  fontStyle:        "normal",
                  letterSpacing:    "0.18em",
                  color:            logoColor,
                  textTransform:    "uppercase",
                  fontFamily:       FONT_DISPLAY,
                  lineHeight:       1,
                  transition:       "color 0.2s, text-shadow 0.2s, background-size 0.25s",
                  textShadow:       logoHovered
                    ? `0 0 14px rgba(${ACCENT_RGB},0.55)`
                    : `0 0 8px rgba(${ACCENT_RGB},0.20)`,
                  paddingBottom:    2,
                  backgroundImage:  `linear-gradient(${logoColor}, ${logoColor})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "0 100%",
                  backgroundSize:   logoHovered ? "100% 2px" : "0% 2px",
                }}>
                  Yogyism
                </span>
                <ExternalLinkIcon size={13} color={logoColor} />
              </div>
            )}
          </a>
        </div>

        {/* ── Center: "Dreamfulness" title — grid column 2. Always the true
             center now, on mobile and desktop, since the outer columns are
             forced to equal (1fr) widths above. ── */}
        <div
          style={{
            justifySelf: "center",
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            zIndex: 1,

            fontFamily: FONT_DISPLAY,
            fontStyle: "normal",
            fontWeight: 600,
            fontSize: "clamp(16px,1.8vw,26px)",
            letterSpacing: 0.5,
            color: ACCENT,
          }}
        >
          Dreamfulness
        </div>

        {/* ── Right: buttons + user — grid column 3, pinned to the end ── */}
        {!isMobile && (
        <div style={{
          display:    "flex",
          alignItems: "center",
          gap:        "clamp(8px,1vw,14px)",
          zIndex:     1,
          justifySelf: "end",
        }}>
         
                    {/* Support → PayPal */}
                    <a
            href="https://ko-fi.com/yogyism"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...btnBase, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, lineHeight: 1 }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.borderColor = `rgba(${ACCENT_RGB},0.75)`; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.borderColor = `rgba(${ACCENT_RGB},0.55)`; }}
          >
            <span style={{ color: "#ff5a6e", fontSize: "1.2em", lineHeight: 1 }}>♥</span>
            Support US
            <span style={{ color: "#ff5a6e", fontSize: "1.2em", lineHeight: 1 }}>♥</span>
            <ExternalLinkIcon size={11} color="currentColor" />
          </a>

            {/* Contact Us → opens ContactModal */}
            <button
              style={btnBase}
              onClick={() => setShowContact(true)}
              onMouseEnter={btnHover}
              onMouseLeave={btnLeave}
            >
              Contact Us
            </button>

            {user ? (
              /* Logged-in: avatar + name + logout */
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {user.photoURL && (
                    <img
                      src={user.photoURL}
                      alt=""
                      referrerPolicy="no-referrer"
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        border: "1px solid rgba(55,138,221,0.4)",
                      }}
                    />
                  )}
                  <div style={{ lineHeight: 1.2 }}>
                    <div style={{ fontSize: "clamp(11px,0.9vw,14px)", color: "#e8f4ff", fontFamily: FONT }}>
                      {user.displayName}
                    </div>
                    <div style={{ fontSize: "clamp(9px,0.75vw,11px)", color: "#7aa8cc" }}>
                      {user.email}
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  style={{
                    ...btnBase,
                    border: "1px solid rgba(220,90,90,0.6)",
                    color:  "#ff9a9a",
                    transition: "border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.opacity     = "1";
                    e.currentTarget.style.borderColor = "rgba(220,90,90,0.85)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.opacity     = "0.85";
                    e.currentTarget.style.borderColor = "rgba(220,90,90,0.6)";
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              /* Not logged in: Login button (consent-gated) */
              <button
                style={btnBase}
                onClick={handleLoginClick}
                onMouseEnter={btnHover}
                onMouseLeave={btnLeave}
              >
                Login
              </button>
            )}
          </div>
        )}

        {/* ── Right: compact overflow menu, mobile only ── */}
        {isMobile && (
          <MobileHeaderMenu
            user={user}
            onLogin={handleLoginClick}
            onLogout={handleLogout}
            onContact={() => setShowContact(true)}
          />
        )}
      </div>

      {/* ── Modals ── */}
      {showConsent && (
        <ConsentModal
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
        />
      )}

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
      />

      <ContactModal
        isOpen={showContact}
        onClose={() => setShowContact(false)}
        accent={ACCENT}
        accentRgb={ACCENT_RGB}
      />
    </>
  );
}